import React from 'react';
import { industries, localize } from '../mock';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import translations from '../translations';
import SEO from '../components/SEO';
import { SectionHero, SectionCTA, SectionHeading } from '../components/Sections';

const IndustryDetail = ({ industry, index, lang, t }) => {
  const localBenefits = lang === 'es' ? industry.benefits_es : industry.benefits;
  return (
    <div className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
      <div className={index % 2 === 1 ? 'md:order-2' : ''}>
        <div className="relative group">
          <img src={industry.image} alt={localize(industry, 'name', lang)} className="rounded-2xl shadow-2xl w-full h-[400px] object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-orange-500/20 to-transparent rounded-2xl"></div>
        </div>
      </div>
      <div className={index % 2 === 1 ? 'md:order-1' : ''}>
        <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
          {localize(industry, 'name', lang)}
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-6">
          {t.transforming} <span className="text-orange-500">{localize(industry, 'name', lang)}</span>
        </h2>
        <p className="text-lg text-gray-600 mb-8">{localize(industry, 'description', lang)}</p>
        <div className="space-y-4 mb-8">
          <h3 className="text-xl font-semibold text-gray-900">{t.keyBenefits}</h3>
          {localBenefits.map((benefit) => (
            <div key={benefit} className="flex items-start space-x-3">
              <CheckCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">{benefit}</span>
            </div>
          ))}
        </div>
        <Link to="/contact">
          <Button data-testid={`industry-cta-${industry.id}`} className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-6 rounded-lg transition-all duration-300 hover:shadow-xl group">
            {t.learnMoreSolutions}
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

const UseCaseCard = ({ useCase }) => (
  <Card className="hover:shadow-2xl transition-all duration-500 border-0">
    <CardContent className="p-8">
      <div className="bg-orange-100 w-12 h-12 rounded-lg flex items-center justify-center mb-6">
        <span className="text-2xl font-bold text-orange-500">{useCase.number}</span>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4">{useCase.title}</h3>
      <p className="text-gray-600 mb-4">{useCase.description}</p>
      <div className="text-sm text-orange-500 font-semibold">{useCase.tag}</div>
    </CardContent>
  </Card>
);

const Industries = () => {
  const { lang } = useLanguage();
  const t = translations[lang].industries;

  return (
    <div className="min-h-screen">
      <SEO path="/industries" />
      <SectionHero title1={t.heroTitle} titleAccent={t.heroTitleAccent} title2="" description={t.heroDescription} />

      <section data-testid="industries-detail" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {industries.map((industry, index) => (
              <IndustryDetail key={industry.id} industry={industry} index={index} lang={lang} t={t} />
            ))}
          </div>
        </div>
      </section>

      <section data-testid="use-cases" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading title={t.applicationsTitle} titleAccent={t.applicationsTitleAccent} subtitle={t.applicationsSubtitle} />
          <div className="grid md:grid-cols-2 gap-8">
            {t.useCases.map((useCase) => (
              <UseCaseCard key={useCase.number} useCase={useCase} />
            ))}
          </div>
        </div>
      </section>

      <SectionCTA title={t.ctaTitle} description={t.ctaDescription} buttonText={t.scheduleConsultation} buttonLink="/contact" testId="industries-schedule-btn" />
    </div>
  );
};

export default Industries;
